import type { EntityType, FlatContentLayersType } from "@config";
import { current } from "immer";
import type { StoryStateType } from "../types";

export const updateActiveState = (
  state: StoryStateType,
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
): void => {
  const { entity: activeEntity } = getActive(entity, flatContentLayers);
  state.activeEntityId = activeEntity.id;
  if (activeEntity.type === "slide") state.activeThumbnailId = activeEntity.id;
};

export const updateBroadcastActiveState = (
  state: StoryStateType,
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
  payloadId: string,
): void => {
  const activeEntity = flatContentLayers[state.activeEntityId || ""];
  if (!activeEntity) return;

  const entityToDelete = flatContentLayers[payloadId];
  if (entityToDelete?.type === "element") {
    updateActiveState(state, entity, flatContentLayers);
    return;
  }

  let currentParent = activeEntity;
  while (currentParent.parentId) {
    if (currentParent.parentId === payloadId) {
      state.activeEntityId = payloadId;
      break;
    }
    currentParent = flatContentLayers[currentParent.parentId];
    if (!currentParent) break;
  }

  updateActiveState(state, entity, flatContentLayers);
};

export const removeEntityFromParent = (
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
): void => {
  const parentId = entity.parentId || "";
  const parent = flatContentLayers[parentId];
  if (parent) {
    parent.childIds = parent.childIds.filter(
      (childId) => childId !== entity.id,
    );
  }
};

export const deleteChildEntities = (
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
  deletedEntities: Record<string, EntityType> = {},
): void => {
  if (!entity.childIds) return;
  entity.childIds.forEach((childId) => {
    const child = flatContentLayers[childId];
    if (!child) return;
    deleteChildEntities(child, flatContentLayers, deletedEntities);
    deletedEntities[childId] = current(child);
    delete flatContentLayers[childId];
  });
  deletedEntities[entity.id] = current(entity);
  delete flatContentLayers[entity.id];
};

function getActive(
  entity: EntityType,
  flatContentLayers: FlatContentLayersType,
) {
  const parentId = entity.parentId || "";
  const parent = flatContentLayers[parentId];
  const index = parent.childIds.indexOf(entity.id);

  if (entity.type === "element") {
    // Try to find next unlocked element
    for (let i = index + 1; i < parent.childIds.length; i++) {
      const nextId = parent.childIds[i];
      if (!isLockedCollaboration(nextId)) {
        return { entity: flatContentLayers[nextId] };
      }
    }
    // If no unlocked next element, try previous elements
    for (let i = index - 1; i >= 0; i--) {
      const prevId = parent.childIds[i];
      if (!isLockedCollaboration(prevId)) {
        return { entity: flatContentLayers[prevId] };
      }
    }
    // If all siblings are locked, return parent (original behavior)
    const parentEntity =
      parent.type === "group"
        ? flatContentLayers[parent.parentId || ""]
        : parent;
    return { entity: parentEntity };
  }

  // Original behavior for non-element types
  const active =
    index === parent.childIds.length - 1
      ? parent.childIds[index - 1]
      : parent.childIds[index + 1];
  if (!active) {
    const parentEntity =
      parent.type === "group"
        ? flatContentLayers[parent.parentId || ""]
        : parent;
    return { entity: parentEntity };
  }
  return { entity: flatContentLayers[active] };
}

function isLockedCollaboration(id: string) {
  return !!document.querySelector(`[data-disabled-collaboration="${id}"]`);
}
